import React, { useState, useEffect, useRef, useMemo, useCallback } from "react"
import { useSelector } from "react-redux"
import "./LibraryComponent.css"
import { message as antdMessage } from "antd"

import PageHeader from "../../components/Common/PageHeader"
import EmptyState from "../../components/Common/EmptyState"
import UploadImagesModal from "../../components/Common/Modals/UploadImagesModal"
import { LibraryApi } from "../../Apis/libraryApi"
import Masonry from "react-masonry-css"
import throttle from "lodash/throttle"
import { translations } from "../../utils/Translation/staticTranslation"
import DeleteConfirmationModal from "../../components/Common/Modals/DeleteConfirmationModal"
import { getUserCookies } from "../../utils/Cookies"
import { jwtDecode } from "jwt-decode"
import {
  isProfileModuleDeleteBlocked,
  isProfileModuleReadOnly,
} from "../../utils/modulePermissionSidebar"

const lang = localStorage.getItem("language") || "ar"
const libraryPage = translations[lang]?.librayPage

// Use translations for dynamic option creation
const getThemeStyles = () => [
  { value: "All", label: libraryPage?.theme?.all || "All" },
  { value: "Dark", label: libraryPage?.theme?.dark || "Dark" },
  { value: "Light", label: libraryPage?.theme?.light || "Light" },
  { value: "Moroccan", label: libraryPage?.theme?.moroccan || "Moroccan" },
]

const getDecorStyles = () => [
  { value: "All", label: libraryPage?.decor_style?.all || "All" },
  { value: "Islamic", label: libraryPage?.decor_style?.islamic || "Islamic" },
  {
    value: "Minimalist",
    label: libraryPage?.decor_style?.minimalist || "Minimalist",
  },
  {
    value: "Tropical",
    label: libraryPage?.decor_style?.tropical || "Tropical",
  },
  { value: "Boho", label: libraryPage?.decor_style?.boho || "Boho" },
  { value: "Modern", label: libraryPage?.decor_style?.modern || "Modern" },
  {
    value: "Contemporary",
    label: libraryPage?.decor_style?.contemporary || "Contemporary",
  },
  { value: "Classy", label: libraryPage?.decor_style?.classy || "Classic" },
  {
    value: "Modern_Classic",
    label: libraryPage?.decor_style?.modern_classic || "Modern Classic",
  },
  {
    value: "Industrial",
    label: libraryPage?.decor_style?.industrial || "Industrial",
  },
  {
    value: "Rustic_Modern",
    label: libraryPage?.decor_style?.rustic_modern || "Rustic Modern",
  },
  {
    value: "Eclectic",
    label: libraryPage?.decor_style?.eclectic || "Eclectic",
  },
  { value: "Zen", label: libraryPage?.decor_style?.zen || "Zen" },
  {
    value: "Shabby_Chic",
    label: libraryPage?.decor_style?.shabby_chic || "Shabby Chic",
  },
]

const getRoomTypes = () => [
  { value: "All", label: libraryPage?.room_types?.all || "All" },
  { value: "Office", label: libraryPage?.room_types?.office || "Office" },
  { value: "Library", label: libraryPage?.room_types?.library || "Library" },
  { value: "Gym", label: libraryPage?.room_types?.gym || "Gym" },
  {
    value: "Restaurants",
    label: libraryPage?.room_types?.restaurant || "Restaurants",
  },
  { value: "Kitchen", label: libraryPage?.room_types?.kitchen || "Kitchen" },
  {
    value: "Living_Room",
    label: libraryPage?.room_types?.living_room || "Living Room",
  },
  {
    value: "Kids_Room",
    label: libraryPage?.room_types?.kids_room || "Kids Room",
  },
  { value: "Bedroom", label: libraryPage?.room_types?.bedroom || "Bedroom" },
  { value: "Dining", label: libraryPage?.room_types?.dining || "Dining Room" },
  { value: "Entrance", label: libraryPage?.room_types?.entrance || "Entrance" },
  {
    value: "Garden",
    label: libraryPage?.room_types?.garden || "Garden/Outdoor",
  },
  { value: "Bathroom", label: libraryPage?.room_types?.bathroom || "Bathroom" },
  {
    value: "Gaming_Room",
    label: libraryPage?.room_types?.gaming_room || "Gaming Room",
  },
]

const shuffleArray = (array) => {
  const newArray = [...array]
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[newArray[i], newArray[j]] = [newArray[j], newArray[i]]
  }
  return newArray
}

const Notification = ({ type, message, onClose }) => {
  return (
    <div className={`notification notification-${type}`}>
      <span>{message}</span>
      <button className="notification-close" onClick={onClose}>
        ×
      </button>
    </div>
  )
}

const FilterSection = React.memo(
  ({ title, options, activeOption, setOption }) => (
    <div className="filter-section">
      <span className="filter-label">{title}:</span>
      <div className="filter-options">
        {options.map((option) => (
          <button
            key={option.value}
            className={`filter-tag ${
              activeOption === option.value ? "filter-tag-active" : ""
            }`}
            onClick={() => setOption(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  )
)

const LibraryComponent = () => {
  const user = useSelector((state) => state.user.user)
  const token = getUserCookies()?.token
  const decoded = token ? jwtDecode(token) : null
  const market_role = decoded?.market_role
  const isLibraryReadOnly =
    market_role === "2" ||
    market_role === "3" ||
    isProfileModuleReadOnly(user, "library")
  const isLibraryDeleteBlocked =
    isLibraryReadOnly || isProfileModuleDeleteBlocked(user, "library")

  const [roomType, setRoomType] = useState("All")
  const [decoreStyle, setDecoreStyle] = useState("All")
  const [theme, setTheme] = useState("All")
  const [isModalOpen, setIsModalOpen] = useState(false)

  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [imageToDelete, setImageToDelete] = useState(null)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const [images, setImages] = useState([])
  const [loadedImages, setLoadedImages] = useState(new Set())
  const [imageAspectRatios, setImageAspectRatios] = useState({})

  const [isInitialLoading, setIsInitialLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const [notification, setNotification] = useState({
    show: false,
    type: "",
    message: "",
  })

  const PAGE_SIZE = 100
  const [hasMore, setHasMore] = useState(true)
  const [offset, setOffset] = useState(0)

  // Get translated filter options
  const [filters, setFilters] = useState({
    roomTypes: getRoomTypes(),
    decoreStyles: getDecorStyles(),
    themes: getThemeStyles(),
  })

  // Update filter options when language changes
  // useEffect(() => {
  //   const currentLang = localStorage.getItem("language") || "en"
  //   setFilters({
  //     roomTypes: getRoomTypes(),
  //     decoreStyles: getDecorStyles(),
  //     themes: getThemeStyles(),
  //   })
  // }, [lang])

  const closeModal = () => setIsModalOpen(false)

  const handleUploadSuccess = () => {
    // Close modal and refresh the current filtered view
    setIsModalOpen(false)
    setOffset(0)
    fetchFilteredData() // keeps current filters; use fetchInitialData() if you want "All"
  }

  const isInitialMount = useRef(true)
  const galleryRef = useRef(null)
  const handleRoomTypeChange = useCallback((value) => {
    setRoomType(value)
  }, [])

  const handleDecoreStyleChange = useCallback((value) => {
    setDecoreStyle(value)
  }, [])

  const handleThemeChange = useCallback((value) => {
    setTheme(value)
  }, [])

  const [windowSize, setWindowSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 1200,
    height: typeof window !== "undefined" ? window.innerHeight : 800,
  })

  useEffect(() => {
    const handleResize = throttle(() => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }, 200)

    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleResize)
      return () => window.removeEventListener("resize", handleResize)
    }
  }, [])

  const getColumnCount = (width) => {
    if (!width) return 4
    if (width <= 768) return 2
    if (width <= 900) return 2
    if (width <= 1400) return 3
    if (width <= 1600) return 4
    return 4
  }

  const columnCount = useMemo(
    () => getColumnCount(windowSize.width),
    [windowSize.width]
  )

  const breakpointColumnsObj = useMemo(
    () => ({
      default: columnCount,
      1600: 4,
      1400: 3,
      768: 2,
    }),
    [columnCount]
  )

  useEffect(() => {
    fetchInitialData()
  }, [])

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    const timer = setTimeout(() => {
      setOffset(0)
      fetchFilteredData()
    }, 0)

    return () => clearTimeout(timer)
  }, [roomType, decoreStyle, theme])

  useEffect(() => {
    const imageContainer = galleryRef.current

    const handleContainerScroll = throttle(() => {
      if (!imageContainer || isLoadingMore || !hasMore) return

      const scrollPosition = imageContainer.scrollTop
      const containerHeight = imageContainer.clientHeight
      const scrollHeight = imageContainer.scrollHeight
      const scrollThreshold = scrollHeight - scrollPosition - containerHeight

      if (scrollThreshold < 100 && !isLoadingMore && hasMore) {
        loadMoreImages()
      }
    }, 200)

    if (imageContainer) {
      imageContainer.addEventListener("scroll", handleContainerScroll)
    }

    return () => {
      if (imageContainer) {
        imageContainer.removeEventListener("scroll", handleContainerScroll)
      }
    }
  }, [hasMore, isLoadingMore, offset])

  const loadMoreImages = async () => {
    if (isLoadingMore || !hasMore) return

    try {
      setIsLoadingMore(true)
      // console.log('Loading more images...')

      const newOffset = offset + PAGE_SIZE

      const filterParams = {
        room_type: roomType === "All" ? "All" : roomType,
        room_style: decoreStyle === "All" ? "All" : decoreStyle,
        room_theme: theme === "All" ? "All" : theme,
        limit: 500,
      }

      const response = await LibraryApi.filterSelection(filterParams)

      if (
        response &&
        response.data?.results &&
        Array.isArray(response.data?.results)
      ) {
        if (response.data?.results.length > 0) {
          const newImages = response.data?.results.map((item) => ({
            id: item.id,
            src: item.img_url,
            style: item.room_style.toLowerCase().replace(/_/g, " "),
            roomType: item.room_type.toLowerCase().replace(/_/g, " "),
            theme: item.room_theme.toLowerCase().replace(/_/g, " "),
            description: `${item.room_style
              .toLowerCase()
              .replace(/_/g, " ")} design style`,
            uuid: item.uuid,
          }))

          setImages((prevImages) => {
            const existingIds = new Set(prevImages.map((img) => img.id))
            const uniqueNewImages = newImages.filter(
              (img) => !existingIds.has(img.id)
            )

            return shuffleArray([...prevImages, ...uniqueNewImages])
          })

          setOffset(newOffset)

          setHasMore(response.data?.results.length >= PAGE_SIZE)
        } else {
          setHasMore(false)
          // console.log("No more images to load")
        }
      } else {
        setHasMore(false)
      }
    } catch (error) {
      console.error("Error loading more images:", error)
    } finally {
      setIsLoadingMore(false)
    }
  }

  const fetchInitialData = async () => {
    try {
      setIsInitialLoading(true)
      setOffset(0)
      setImages([])

      const initialParams = {
        room_type: "All",
        room_style: "All",
        room_theme: "All",
        limit: 500,
        source: "public",
      }

      const response = await LibraryApi.filterSelection(initialParams)

      if (response?.data?.results && Array.isArray(response.data.results)) {
        const processedImages = response.data.results.map((item) => ({
          id: item.id,
          src: item.img_url,
          style: item.room_style.toLowerCase().replace(/_/g, " "),
          roomType: item.room_type.toLowerCase().replace(/_/g, " "),
          theme: item.room_theme.toLowerCase().replace(/_/g, " "),
          description: `${item.room_style
            .toLowerCase()
            .replace(/_/g, " ")} design style`,
          uuid: item.uuid,
        }))

        setImages(shuffleArray(processedImages))
        setHasMore(response.data.results.length >= PAGE_SIZE)
      } else {
        console.warn("⚠️ No results in response")
        setImages([])
        setHasMore(false)
      }
    } catch (error) {
      console.error("❌ Error fetching initial data:", error)
      setImages([])
      setHasMore(false)
    } finally {
      setIsInitialLoading(false)
    }
  }

  const fetchFilteredData = async () => {
    try {
      if (galleryRef.current) {
        galleryRef.current.scrollTo({
          top: 0,
          behavior: "smooth",
        })
      }
      setIsInitialLoading(true)
      setImages([])
      setOffset(0)

      const filterParams = {
        room_type: roomType === "All" ? "All" : roomType,
        room_style: decoreStyle === "All" ? "All" : decoreStyle,
        room_theme: theme === "All" ? "All" : theme,
        limit: 500,
      }

      const response = await LibraryApi.filterSelection(filterParams)

      if (
        response &&
        response.data?.results &&
        Array.isArray(response.data?.results)
      ) {
        const processedImages = response.data?.results.map((item) => ({
          id: item.id,
          src: item.img_url,
          style: item.room_style.toLowerCase().replace(/_/g, " "),
          roomType: item.room_type.toLowerCase().replace(/_/g, " "),
          theme: item.room_theme.toLowerCase().replace(/_/g, " "),
          description: `${item.room_style
            .toLowerCase()
            .replace(/_/g, " ")} design style`,
          uuid: item.uuid,
        }))

        setImages(shuffleArray(processedImages))

        setHasMore(response.data?.results.length >= PAGE_SIZE)
      } else {
        setImages([])
        setHasMore(false)
      }
    } catch (error) {
      console.error("Error fetching filtered data:", error)
      setHasMore(false)
    } finally {
      setIsInitialLoading(false)
    }
  }

  const showDeleteConfirmation = (imageId, imageSrc, uuid) => {
    if (isLibraryDeleteBlocked) {
      const currentLang = localStorage.getItem("language") || "ar"
      const lp = translations[currentLang]?.librayPage || {}
      antdMessage.warning(
        (isLibraryReadOnly
          ? lp.read_only_cannot_delete
          : translations[currentLang]?.common_error_msg?.delete_not_allowed) ||
          "You cannot delete images from the library."
      )
      return
    }
    setImageToDelete({ id: imageId, src: imageSrc, uuid: uuid })
    setDeleteModalVisible(true)
  }

  const handleCancelDelete = () => {
    setDeleteModalVisible(false)
    setImageToDelete(null)
  }

  const handleConfirmDelete = async () => {
    if (isLibraryDeleteBlocked) return
    if (!imageToDelete) return
    setDeleteLoading(true)
    try {
      const baseUrl = "https://d6p3aufqwq8a9.cloudfront.net/"
      let filePath = ""

      if (imageToDelete.src && imageToDelete.src.startsWith(baseUrl)) {
        filePath = imageToDelete.src.substring(baseUrl.length)
      } else {
        // Fallback to using the full src if it doesn't match expected format
        filePath = imageToDelete.src
      }

      const response = await LibraryApi.deleteLibraryImage({
        file_name: filePath,
      })

      if (response && response.status === 204) {
        // debugger
        setImages(images.filter((image) => image.id !== imageToDelete.id))
        antdMessage.success(libraryPage?.image_deleted_successfully)
      } else {
        throw new Error(libraryPage?.api_returned_unsuccessful_response)
      }
    } catch (error) {
      console.error("Error deleting image:", error)
      antdMessage.error(libraryPage?.failed_to_delete_image)
    } finally {
      setDeleteLoading(false)
      setDeleteModalVisible(false)
      setImageToDelete(null)
    }
  }

  const handleImageLoad = (imageUrl) => {
    setLoadedImages((prev) => {
      const newSet = new Set(prev)
      newSet.add(imageUrl)
      return newSet
    })
  }

  useEffect(() => {
    const unloadedImages = images.filter(
      (image) => !imageAspectRatios[image.src]
    )

    unloadedImages.forEach((image) => {
      const img = new window.Image()
      img.src = image.src
      img.onload = () => {
        setImageAspectRatios((prev) => ({
          ...prev,
          [image.src]: (img.height / img.width) * 100,
        }))
      }
    })
  }, [images, imageAspectRatios])

  const showModal = () => {
    if (isLibraryReadOnly) return
    setIsModalOpen(true)
  }

  const renderFallbackGallery = () => (
    <div className="masonry-gallery-fallback">
      {images.map((image) => (
        <div key={image.id} className="masonry-item">
          <div className="image-container">
            <img
              src={image.src}
              alt={`${image.roomType || ""} design`}
              className="gallery-image"
            />
            <div className="image-overlay">
              {!isLibraryDeleteBlocked && (
                <button
                  className="img-delete-button"
                  onClick={() =>
                    showDeleteConfirmation(image.id, image.src, image.uuid)
                  }
                  aria-label="Delete image"
                  type="button"
                >
                  <img
                    src="/icons/trash-2.svg"
                    alt="Delete"
                    width="16"
                    height="16"
                  />
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  )

  const skeletonItems = Array(16).fill(null)

  // Get translated filter section titles
  const roomTypeTitle = lang === "ar" ? "نوع الغرفة" : "Room Type"
  const decoreStyleTitle = lang === "ar" ? "نمط الديكور" : "Decore Style"
  const themeTitle = lang === "ar" ? "السمة" : "Theme"

  // Get translated empty state messages
  const emptyStateTitle = lang === "ar" ? "لا توجد صور" : "No Images Found"
  const emptyStateDesc =
    lang === "ar"
      ? "يبدو أنه لا توجد صور تطابق مرشحاتك. حاول تعديل اختيارك أو تحميل صور جديدة."
      : "It seems there are no images matching your filters. Try adjusting your selection or upload new images."

  const modal = translations[lang]?.modals
  const deleteModalTitle = lang === "ar" ? "تأكيد الحذف" : "Confirm Delete"
  const deleteModalContent =
    modal?.delete_image_confirmation ||
    "Are you sure you want to delete this image? This action cannot be undone."

  return (
    <div className="library-container">
      <PageHeader
        title={libraryPage?.library || "Library"}
        actionButton={{
          text: libraryPage?.upload_images || "Upload Images",
          onClick: showModal,
        }}
        onAddImages={showModal}
        addImagesDisabled={isLibraryReadOnly}
        addImagesDisabledTitle={
          translations[localStorage.getItem("language") || "ar"]?.librayPage
            ?.read_only_cannot_upload
        }
      />
      <div className="main-content-container" ref={galleryRef}>
        {notification.show && (
          <Notification
            type={notification.type}
            message={notification.message}
            onClose={() =>
              setNotification({ show: false, type: "", message: "" })
            }
          />
        )}

        <div className="filters-container">
          <FilterSection
            title={libraryPage?.room_types_title}
            options={filters.roomTypes}
            activeOption={roomType}
            setOption={handleRoomTypeChange}
          />

          <FilterSection
            title={libraryPage?.decor_style_title}
            options={filters.decoreStyles}
            activeOption={decoreStyle}
            setOption={handleDecoreStyleChange}
          />

          <FilterSection
            title={libraryPage?.theme_title}
            options={filters.themes}
            activeOption={theme}
            setOption={handleThemeChange}
          />
        </div>

        <div className="gallery-images gallery-scroll-images">
          {isInitialLoading ? (
            <div className="masonry-gallery">
              <Masonry
                breakpointCols={breakpointColumnsObj}
                className="masonry-grid"
                columnClassName="masonry-grid_column"
              >
                {skeletonItems.map((_, index) => (
                  <div key={index} className="masonry-grid_item">
                    <div className="image-container">
                      <div
                        className="skeleton-image"
                        style={{
                          paddingBottom: `${
                            Math.floor(Math.random() * 40) + 80
                          }%`,
                          width: "100%",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </Masonry>
            </div>
          ) : images?.length > 0 ? (
            <React.Fragment>
              {typeof Masonry === "function" ? (
                <div className="masonry-gallery">
                  <Masonry
                    breakpointCols={breakpointColumnsObj}
                    className="masonry-grid"
                    columnClassName="masonry-grid_column"
                  >
                    {images.map((image) => (
                      <div key={image.id} className="masonry-grid_item">
                        <div className="image-container">
                          {!loadedImages.has(image.src) && (
                            <div
                              className="skeleton"
                              style={{ paddingBottom: "100%" }}
                            />
                          )}
                          <img
                            src={image.src}
                            alt={`${image.roomType || ""} design`}
                            className={`gallery-image image-loading-transition ${
                              loadedImages.has(image.src) ? "image-loaded" : ""
                            }`}
                            onLoad={() => handleImageLoad(image.src)}
                            loading="lazy"
                          />
                          <div className="image-overlay">
                            {!isLibraryDeleteBlocked && (
                              <button
                                className="img-delete-button"
                                type="button"
                                onClick={() =>
                                  showDeleteConfirmation(
                                    image.id,
                                    image.src,
                                    image.uuid
                                  )
                                }
                                aria-label="Delete image"
                              >
                                <img
                                  src="/icons/trash-2.svg"
                                  alt="Delete"
                                  width="16"
                                  height="16"
                                />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </Masonry>
                </div>
              ) : (
                renderFallbackGallery()
              )}

              {isLoadingMore && (
                <div className="loading-more-container">
                  <div className="loader"></div>
                </div>
              )}
            </React.Fragment>
          ) : (
            <EmptyState
              title={libraryPage?.no_more_images_title}
              description={libraryPage?.no_more_images_description}
              actionButton={{
                text: libraryPage?.upload_images,
                onClick: showModal,
              }}
            />
          )}
        </div>
      </div>

      <UploadImagesModal
        visible={isModalOpen} // if your modal uses "open", pass open={isModalOpen} too
        open={isModalOpen}
        onClose={closeModal}
        onCancel={closeModal}
        onSuccess={handleUploadSuccess} // call this inside the modal after successful upload
      />

      <DeleteConfirmationModal
        deleteLoading={deleteLoading}
        visible={deleteModalVisible}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        confirmDisabled={isLibraryReadOnly}
        title={deleteModalTitle}
        content={deleteModalContent}
      />
    </div>
  )
}

export default LibraryComponent
